import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { getImageUrl } from "@/lib/api";
import {
  Eye, MousePointerClick, TrendingUp, Calendar, MapPin, Users, RefreshCw, ExternalLink, Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, color, label, value, sublabel }) {
  return (
    <div className="bg-white border border-[#E6E6E6] rounded-lg p-5" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color }} />
        <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-3xl font-semibold text-zinc-900 tabular-nums" style={{ fontFamily: 'Outfit' }}>{value}</p>
      {sublabel && <p className="text-[11px] text-zinc-400 mt-1">{sublabel}</p>}
    </div>
  );
}

export default function SponsorDashboardPage() {
  const { adId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [ad, setAd] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAd = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/ads/${adId}`, { params: { token } });
      setAd(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load sponsor dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adId, token]);

  useEffect(() => { fetchAd(); }, [fetchAd]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAd();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#074572] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-10 max-w-md text-center" data-testid="sponsor-error">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Lock size={22} className="text-[#F85037]" />
          </div>
          <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Access Denied</h1>
          <p className="text-sm text-zinc-500 mt-2">{error}</p>
          <p className="text-[11px] text-zinc-400 mt-3">Please contact the SportsUnity team for a valid sponsor link.</p>
        </div>
      </div>
    );
  }

  const imgSrc = ad.image_url?.startsWith("http") ? ad.image_url : getImageUrl(ad.image_url);
  const created = ad.created_at ? new Date(ad.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : "";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Public header */}
      <header className="bg-white border-b border-[#E6E6E6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="sponsor-logo">
            <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </Link>
          <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Sponsor Dashboard</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="sponsor-dashboard">
        {/* Ad hero card */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden mb-6">
          <div className="grid md:grid-cols-[280px_1fr]">
            {imgSrc && (
              <div className="bg-zinc-100">
                <img src={imgSrc} alt={ad.headline} className="w-full h-full object-cover aspect-video md:aspect-auto" />
              </div>
            )}
            <div className="p-6">
              <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Sponsored by {ad.sponsor_name}</p>
              <h1 className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{ad.headline}</h1>
              <p className="text-sm text-zinc-500 mt-2">{ad.description}</p>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-zinc-500">
                <a href={ad.target_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#074572] font-medium hover:underline" data-testid="sponsor-target-url">
                  {ad.target_url} <ExternalLink size={11} />
                </a>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${ad.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {ad.status}
                </span>
                {created && (
                  <span className="flex items-center gap-1 text-zinc-400">
                    <Calendar size={11} /> Live since {created}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Performance</h2>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 text-xs text-[#074572] hover:text-[#053a60] font-medium"
            data-testid="sponsor-refresh"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Eye} color="#074572" label="Impressions" value={(ad.impressions || 0).toLocaleString()} sublabel="Total ad views" />
          <StatCard icon={MousePointerClick} color="#F85037" label="Clicks" value={(ad.clicks || 0).toLocaleString()} sublabel="Total ad clicks" />
          <StatCard icon={TrendingUp} color="#10B981" label="CTR" value={`${(ad.ctr || 0).toFixed(2)}%`} sublabel="Click-through rate" />
        </div>

        {/* Campaign details */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Campaign Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <dt className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Calendar size={11} /> Campaign Period
              </dt>
              <dd className="text-zinc-800">
                {ad.start_date ? new Date(ad.start_date).toLocaleDateString() : "Any time"}
                {" — "}
                {ad.end_date ? new Date(ad.end_date).toLocaleDateString() : "No end date"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Users size={11} /> Audience: Roles
              </dt>
              <dd className="flex flex-wrap gap-1">
                {ad.target_roles?.length > 0 ? (
                  ad.target_roles.map(r => (
                    <span key={r} className="text-[11px] px-2 py-0.5 bg-[#074572]/5 text-[#074572] rounded-full capitalize">{r}</span>
                  ))
                ) : (
                  <span className="text-zinc-500 text-xs">All roles</span>
                )}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <MapPin size={11} /> Audience: Locations
              </dt>
              <dd className="flex flex-wrap gap-1">
                {ad.target_locations?.length > 0 ? (
                  ad.target_locations.map(loc => (
                    <span key={loc} className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full flex items-center gap-0.5">
                      <MapPin size={9} />{loc}
                    </span>
                  ))
                ) : (
                  <span className="text-zinc-500 text-xs">All locations</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        <p className="text-[11px] text-zinc-400 text-center mt-8">
          This dashboard is generated by SportsUnity for the sponsor of this placement. Numbers update in real time.
        </p>
      </main>
    </div>
  );
}
